import { Component } from '@angular/core';
import { TableModule } from 'primeng/table';
import { OtherSkillSuggestion, Technology } from '../../../core/models/technology';
import { TechnologyService } from '../../../core/services/technology.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TechnologyFormComponent } from './technology-form/technology-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';

@Component({
  selector: 'em-technology',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    DialogModule,
    ConfirmDialogModule,
    TranslatePipe,
    EmPermissionDirective
  ],
  templateUrl: './technology.component.html',
  styleUrl: './technology.component.scss',
  providers: [MessageService, ConfirmationService],
})
export class TechnologyComponent {
  technologies!: Technology[];
  otherSkillSuggestions: OtherSkillSuggestion[] = [];
  promotingOtherSkill?: string;
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;
  constructor(
    private technologyService: TechnologyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.refreshTechnologyData();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.technologies.addTechnology.title',
        'layout.configurations.technologies.addTechnology.successMessage.title',
        'layout.configurations.technologies.addTechnology.successMessage.detail',
        'layout.configurations.technologies.editTechnology.title',
        'layout.configurations.technologies.editTechnology.successMessage.title',
        'layout.configurations.technologies.editTechnology.successMessage.detail',
        'layout.configurations.technologies.deleteTechnology.title',
        'layout.configurations.technologies.deleteTechnology.successMessage.title',
        'layout.configurations.technologies.deleteTechnology.successMessage.detail',
        'layout.configurations.technologies.deleteTechnology.message',
        'layout.configurations.technologies.deleteTechnology.yes',
        'layout.configurations.technologies.deleteTechnology.no',
        'layout.configurations.technologies.deleteTechnology.infoMessage.title',
        'layout.configurations.technologies.deleteTechnology.infoMessage.detail',
        'layout.configurations.technologies.deleteTechnology.errorMessage.title',
        'layout.configurations.technologies.deleteTechnology.errorMessage.detail',
        'layout.configurations.technologies.otherSkillSuggestions.successMessage.title',
        'layout.configurations.technologies.otherSkillSuggestions.successMessage.detail',
        'layout.configurations.technologies.otherSkillSuggestions.errorMessage.title',
        'layout.configurations.technologies.otherSkillSuggestions.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllTechnologies() {
    this.technologyService.getAllTechnologies().subscribe((data) => (this.technologies = data));
  }

  getOtherSkillSuggestions() {
    this.technologyService
      .getOtherSkillSuggestions()
      .subscribe((data) => (this.otherSkillSuggestions = data));
  }

  refreshTechnologyData() {
    this.getAllTechnologies();
    this.getOtherSkillSuggestions();
  }

  promoteOtherSkill(suggestion: OtherSkillSuggestion) {
    this.promotingOtherSkill = suggestion.label;
    this.technologyService.promoteOtherSkill(suggestion.label).subscribe({
      next: () => {
        this.refreshTechnologyData();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.technologies.otherSkillSuggestions.successMessage.title'],
          detail: this.translations['layout.configurations.technologies.otherSkillSuggestions.successMessage.detail'],
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.technologies.otherSkillSuggestions.errorMessage.title'],
          detail: this.translations['layout.configurations.technologies.otherSkillSuggestions.errorMessage.detail'],
        });
      },
      complete: () => {
        this.promotingOtherSkill = undefined;
      },
    });
  }

  onAdd() {
    this.ref = this.dialogService.open(TechnologyFormComponent, {
      header: this.translations['layout.configurations.technologies.addTechnology.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((Technolog) => {
      if (Technolog) {
        this.refreshTechnologyData();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.technologies.addTechnology.successMessage.title'],
          detail: this.translations['layout.configurations.technologies.addTechnology.successMessage.detail'],
        });
      }
    });
  }

  onEdit(technology: Technology) {
    this.ref = this.dialogService.open(TechnologyFormComponent, {
      header: this.translations['layout.configurations.technologies.editTechnology.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: technology,
    });

    this.ref.onClose.subscribe((result) => {
    if (!result) return;

    if (result.type === 'updated') {
        this.refreshTechnologyData();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.technologies.editTechnology.successMessage.title'],
          detail: this.translations['layout.configurations.technologies.editTechnology.successMessage.detail'],
        });
      }
      else if (result.type === 'no-change') {
        this.messageService.add({
        severity: 'warn',
        summary:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noChanges'),
        detail:this.translate.instant('layout.configurations.users.assignRolesToUser.roles.noUpdateMessage')
      });
       }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.technologies.deleteTechnology.message'],
      icon: 'pi pi-info-circle',
      header: this.translations['layout.configurations.technologies.deleteTechnology.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.technologies.deleteTechnology.yes'],
      rejectLabel: this.translations['layout.configurations.technologies.deleteTechnology.no'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteTechnology(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.technologies.deleteTechnology.info.title'),
          detail: this.translate.instant('layout.configurations.technologies.deleteTechnology.info.alreadyExists'),

          life: 3000,
        });
      },
    });
  }

  deleteTechnology(id: number) {
    this.technologyService.deleteTechnology(id).subscribe({
      next: () => {
        this.refreshTechnologyData();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.technologies.deleteTechnology.successMessage.title'],
          detail: this.translations['layout.configurations.technologies.deleteTechnology.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting technology: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.technologies.deleteTechnology.errorMessage.title'],
          detail: this.translations['layout.configurations.technologies.deleteTechnology.errorMessage.detail'],
        });
      },
    });
  }
}
